import { ActionType, createAction, createReducer } from 'typesafe-actions';
import { combineReducers } from 'redux';
import { AuthorDetailTimeUsage, ReviewFormFieldError } from './types';
import {
  ValidatorFn,
  authorEmailValidator,
  authorNameValidator,
  bodyTextValidator,
  prosConsValidator,
  totalScoreValidator,
} from './validators';

export const setTotalScore = createAction('reviews/REVIEW_FORM/setTotalScore')<number>();
export const setReviewBodyText = createAction('reviews/REVIEW_FORM/setReviewBodyText')<string>();
export const setReviewProsText = createAction('reviews/REVIEW_FORM/setReviewProsText')<string>();
export const setReviewConsText = createAction('reviews/REVIEW_FORM/setReviewConsText')<string>();
export const setAuthorName = createAction('reviews/REVIEW_FORM/setAuthorName')<string>();
export const setAuthorEmail = createAction('reviews/REVIEW_FORM/setAuthorEmail')<string>();
export const setAuthorDetailShop = createAction('reviews/REVIEW_FORM/setAuthorDetailShop')<string | null>();
export const setAuthorDetailTimeUsage = createAction('reviews/REVIEW_FORM/setAuthorDetailTimeUsage')<{
  timeUsage: AuthorDetailTimeUsage['value'];
  indexFromOne: number;
}>();

export type TotalScoreActions = ActionType<typeof setTotalScore>;
export const totalScore = createReducer<number | null, TotalScoreActions>(null).handleAction(
  setTotalScore,
  (_, { payload }) => payload,
);

export type BodyTextActions = ActionType<typeof setReviewBodyText>;
export const bodyText = createReducer<string | null, BodyTextActions>(null).handleAction(
  setReviewBodyText,
  (_, { payload }) => payload,
);

export type ProsTextActions = ActionType<typeof setReviewProsText>;
export const prosText = createReducer<string | null, ProsTextActions>(null).handleAction(
  setReviewProsText,
  (_, { payload }) => payload,
);

export type ConsTextActions = ActionType<typeof setReviewConsText>;
export const consText = createReducer<string | null, ConsTextActions>(null).handleAction(
  setReviewConsText,
  (_, { payload }) => payload,
);

export type AuthorDetailTimeUsageActions = ActionType<typeof setAuthorDetailTimeUsage>;
export const authorTimeUsage = createReducer<AuthorDetailTimeUsage | null, AuthorDetailTimeUsageActions>(
  null,
).handleAction(setAuthorDetailTimeUsage, (_, { payload }) => ({
  key: 'time_usage',
  value: payload.timeUsage,
}));

export type AuthorDetailShopActions = ActionType<typeof setAuthorDetailShop>;
export const authorShop = createReducer<string | null, AuthorDetailShopActions>(null).handleAction(
  setAuthorDetailShop,
  (_, { payload }) => payload,
);

export type AuthorNameActions = ActionType<typeof setAuthorName>;
export const authorName = createReducer<string | null, AuthorNameActions>(null).handleAction(
  setAuthorName,
  (_, { payload }) => payload,
);

export type AuthorEmailActions = ActionType<typeof setAuthorEmail>;
export const authorEmail = createReducer<string | null, AuthorEmailActions>(null).handleAction(
  setAuthorEmail,
  (_, { payload }) => payload,
);

type RequiredFieldsAction = TotalScoreActions | BodyTextActions | AuthorNameActions | AuthorEmailActions;
export const requiredFieldsAreEmpty = createReducer<boolean, RequiredFieldsAction>(false)
  .handleAction([setTotalScore], (state, { payload }) => state || payload === 0)
  .handleAction(
    [setReviewBodyText, setAuthorName, setAuthorEmail],
    (state, { payload }) => state || payload.length === 0,
  );

const fieldValidatorAsReducer =
  <T>(fieldKey: ReviewFormFieldError['fieldKey'], validatorFn: ValidatorFn<T>) =>
  (state: ReviewFormFieldError[], { payload }: { payload: T }) => {
    const result = state.filter((r) => r.fieldKey !== fieldKey);
    result.push(...validatorFn(payload));
    return result;
  };

type FieldsErrorsActions =
  | TotalScoreActions
  | BodyTextActions
  | ProsTextActions
  | ConsTextActions
  | AuthorNameActions
  | AuthorEmailActions;
const fieldsErrors = createReducer<ReviewFormFieldError[], FieldsErrorsActions>([])
  .handleAction(setTotalScore, fieldValidatorAsReducer('rating', totalScoreValidator('rating')))
  .handleAction(setReviewBodyText, fieldValidatorAsReducer('body', bodyTextValidator('body')))
  .handleAction(setReviewProsText, fieldValidatorAsReducer('pros', prosConsValidator('pros')))
  .handleAction(setReviewConsText, fieldValidatorAsReducer('cons', prosConsValidator('cons')))
  .handleAction(setAuthorName, fieldValidatorAsReducer('author_name', authorNameValidator('author_name')))
  .handleAction(
    setAuthorEmail,
    fieldValidatorAsReducer('author_email', authorEmailValidator('author_email')),
  );

export type FormReducerActions =
  | TotalScoreActions
  | BodyTextActions
  | ProsTextActions
  | ConsTextActions
  | AuthorDetailTimeUsageActions
  | AuthorDetailShopActions
  | AuthorNameActions
  | AuthorEmailActions;

export const formState = combineReducers({
  totalScore,
  bodyText,
  prosText,
  consText,
  authorTimeUsage,
  authorShop,
  authorName,
  authorEmail,
  fieldsErrors,
  requiredFieldsAreEmpty,
});

export type FormState = ReturnType<typeof formState>;
